import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { UploadsDragAndDrop } from "@/components/ui/UploadsDragAndDrop";
import { downloadFile } from "@/lib/api";
import { Download, Eye, FileText, Trash2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { CriarPacienteFormData } from "../schemas/paciente.schema";

interface AbaDocumentosProps {
  form: UseFormReturn<CriarPacienteFormData>;
  formData: CriarPacienteFormData;
}

export const AbaDocumentos = ({ 
  form,
  formData
}: AbaDocumentosProps) => {
  // Métodos para gerenciar documentos
  const adicionarDocumento = () => {
    const documentosAtuais = form.getValues("documentos") || [];
    form.setValue("documentos", [
      ...documentosAtuais,
      {
        tipo_documento: "RG",
        arquivo: null,
        nome_arquivo: "",
      },
    ]);
  };

  const removerDocumento = (index: number) => {
    const documentosAtuais = form.getValues("documentos") || [];
    const novosDocumentos = documentosAtuais.filter((_, i) => i !== index);
    form.setValue("documentos", novosDocumentos);
  };

  const atualizarDocumento = (index: number, campo: string, valor: string | File | null) => {
    const documentosAtuais = form.getValues("documentos") || [];
    const novosDocumentos = [...documentosAtuais];
    novosDocumentos[index] = { ...novosDocumentos[index], [campo]: valor };
    form.setValue("documentos", novosDocumentos);
  };

  const handleFileUpload = (index: number, file: File) => {
    // Atualiza tanto o arquivo quanto o nome do arquivo simultaneamente
    const documentosAtuais = form.getValues("documentos") || [];
    const novosDocumentos = [...documentosAtuais];
    novosDocumentos[index] = {
      ...novosDocumentos[index],
      arquivo: file,
      nome_arquivo: file.name, // Sempre preenche o nome do arquivo com o nome do arquivo selecionado
    };
    form.setValue("documentos", novosDocumentos);
  };
  const handleFileSelect = (tipoDocumento: string) => (file: File) => {
    console.log("📎 Arquivo selecionado:", file.name, "Tipo:", tipoDocumento);
    const documentosAtuais = form.getValues("documentos") || [];
    const docExistente = documentosAtuais.find(d => d.tipo_documento === tipoDocumento);
    
    if (docExistente) {
      const index = documentosAtuais.indexOf(docExistente);
      // Atualiza o documento existente preservando o ID e caminho_arquivo se existirem
      const novosDocumentos = [...documentosAtuais];
      
      // Se o documento já tem nome_arquivo mas não tem arquivo novo, preserva o nome
      // Caso contrário, usa o nome do arquivo selecionado
      const nomeArquivo = file.name || docExistente.nome_arquivo || `documento_${tipoDocumento}`;
      
      novosDocumentos[index] = {
        ...novosDocumentos[index],
        arquivo: file,
        nome_arquivo: nomeArquivo, // Sempre atualiza o nome do arquivo
      };
      console.log("✅ Documento atualizado:", novosDocumentos[index]);
      form.setValue("documentos", novosDocumentos, { shouldValidate: true });
    } else {
      // Cria novo documento com o nome do arquivo
      const novoDocumento = { 
        tipo_documento: tipoDocumento, 
        arquivo: file, 
        nome_arquivo: file.name // Preenche automaticamente o nome do arquivo
      };
      console.log("✅ Novo documento criado:", novoDocumento);
      form.setValue("documentos", [
        ...documentosAtuais,
        novoDocumento
      ], { shouldValidate: true });
    }
  };

  const getCurrentFile = (tipoDocumento: string) => {
    const documentosAtuais = form.getValues("documentos") || [];
    const documento = documentosAtuais.find(d => d.tipo_documento === tipoDocumento);
    
    // Se encontrou um documento mas não tem nome_arquivo e tem caminho_arquivo, extrai do caminho
    if (documento && !documento.nome_arquivo && documento.caminho_arquivo) {
      const partesCaminho = documento.caminho_arquivo.split('/');
      documento.nome_arquivo = partesCaminho[partesCaminho.length - 1] || `documento_${tipoDocumento}`;
    }
    
    return documento;
  };

  const removerArquivo = async (tipoDocumento: string) => {
    const documento = getCurrentFile(tipoDocumento);
    
    // Se o documento tem ID e caminho_arquivo, significa que está salvo no backend
    if (documento?.id && documento?.caminho_arquivo) {
      // Aqui você pode chamar uma API para deletar do servidor se necessário
      // Por enquanto, apenas remove do formulário
    }
    
    const documentosAtuais = form.getValues("documentos") || [];
    form.setValue("documentos", documentosAtuais.filter(d => d.tipo_documento !== tipoDocumento));
  };

  const formatFileSize = (bytes: number) => {
    const sizeMB = (bytes / (1024 * 1024)).toFixed(2);
    return `${sizeMB}MB`;
  };

  const visualizarDocumento = async (documento: { arquivo?: File | null; caminho_arquivo?: string; nome_arquivo?: string }) => {
    // Se for um arquivo novo (File object)
    if (documento.arquivo instanceof File) {
      const url = URL.createObjectURL(documento.arquivo);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);
      return;
    }

    // Se for um documento salvo (tem caminho_arquivo)
    if (documento.caminho_arquivo) {
      try {
        // Descriptografa e obtém o arquivo do backend
        const blob = await downloadFile(`/api/upload/download/${encodeURIComponent(documento.caminho_arquivo)}`);
        
        // Cria uma URL temporária e abre em nova aba para visualização
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (error: any) {
        console.error('Erro ao visualizar documento:', error);
        alert(`Erro ao visualizar documento: ${error.message || 'Erro desconhecido'}`);
      }
    }
  };

  const downloadDocumento = async (documento: { arquivo?: File | null; caminho_arquivo?: string; nome_arquivo?: string }) => {
    // Se for um arquivo novo (File object)
    if (documento.arquivo instanceof File) {
      const url = URL.createObjectURL(documento.arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = documento.nome_arquivo || 'documento';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
      return;
    }

    // Se for um documento salvo (tem caminho_arquivo)
    if (documento.caminho_arquivo) {
      try {
        // Faz download do arquivo descriptografado do backend
        const blob = await downloadFile(`/api/upload/download/${encodeURIComponent(documento.caminho_arquivo)}`);
        
        // Cria uma URL temporária e força download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = documento.nome_arquivo || 'documento';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (error) {
        console.error('Erro ao fazer download do documento:', error);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documentos do Paciente</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* RG */}
        {!getCurrentFile("RG") ? (
          <UploadsDragAndDrop
            label="RG (Registro Geral)"
            onFileSelect={handleFileSelect("RG")}
            currentFile={null}
          />
        ) : (
          <div className="space-y-2">
            <Label className="text-base font-semibold">RG (Registro Geral)</Label>
            <div className="p-4 border rounded-lg bg-muted/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{getCurrentFile("RG")?.nome_arquivo}</p>
                  <p className="text-sm text-muted-foreground">
                    {getCurrentFile("RG")?.arquivo && formatFileSize(getCurrentFile("RG")!.arquivo!.size)}
                    {getCurrentFile("RG")?.caminho_arquivo && "Arquivo salvo"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => visualizarDocumento(getCurrentFile("RG")!)}
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadDocumento(getCurrentFile("RG")!)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerArquivo("RG")}
                  title="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* CPF */}
        {!getCurrentFile("CPF") ? (
          <UploadsDragAndDrop
            label="CPF (Cadastro de Pessoa Física)"
            onFileSelect={handleFileSelect("CPF")}
            currentFile={null}
          />
        ) : (
          <div className="space-y-2">
            <Label className="text-base font-semibold">CPF (Cadastro de Pessoa Física)</Label>
            <div className="p-4 border rounded-lg bg-muted/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{getCurrentFile("CPF")?.nome_arquivo}</p>
                  <p className="text-sm text-muted-foreground">
                    {getCurrentFile("CPF")?.arquivo && formatFileSize(getCurrentFile("CPF")!.arquivo!.size)}
                    {getCurrentFile("CPF")?.caminho_arquivo && "Arquivo salvo"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => visualizarDocumento(getCurrentFile("CPF")!)}
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadDocumento(getCurrentFile("CPF")!)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerArquivo("CPF")}
                  title="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Cartão SUS */}
        {!getCurrentFile("CARTAO_SUS") ? (
          <UploadsDragAndDrop
            label="Cartão SUS"
            onFileSelect={handleFileSelect("CARTAO_SUS")}
            currentFile={null}
          />
        ) : (
          <div className="space-y-2">
            <Label className="text-base font-semibold">Cartão SUS</Label>
            <div className="p-4 border rounded-lg bg-muted/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{getCurrentFile("CARTAO_SUS")?.nome_arquivo}</p>
                  <p className="text-sm text-muted-foreground">
                    {getCurrentFile("CARTAO_SUS")?.arquivo && formatFileSize(getCurrentFile("CARTAO_SUS")!.arquivo!.size)}
                    {getCurrentFile("CARTAO_SUS")?.caminho_arquivo && "Arquivo salvo"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => visualizarDocumento(getCurrentFile("CARTAO_SUS")!)}
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadDocumento(getCurrentFile("CARTAO_SUS")!)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerArquivo("CARTAO_SUS")}
                  title="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Comprovante de Endereço */}
        {!getCurrentFile("COMPROVANTE_ENDERECO") ? (
          <UploadsDragAndDrop
            label="Comprovante de Endereço"
            onFileSelect={handleFileSelect("COMPROVANTE_ENDERECO")}
            currentFile={null}
          />
        ) : (
          <div className="space-y-2">
            <Label className="text-base font-semibold">Comprovante de Endereço</Label>
            <div className="p-4 border rounded-lg bg-muted/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">{getCurrentFile("COMPROVANTE_ENDERECO")?.nome_arquivo}</p>
                  <p className="text-sm text-muted-foreground">
                    {getCurrentFile("COMPROVANTE_ENDERECO")?.arquivo && formatFileSize(getCurrentFile("COMPROVANTE_ENDERECO")!.arquivo!.size)}
                    {getCurrentFile("COMPROVANTE_ENDERECO")?.caminho_arquivo && "Arquivo salvo"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => visualizarDocumento(getCurrentFile("COMPROVANTE_ENDERECO")!)}
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadDocumento(getCurrentFile("COMPROVANTE_ENDERECO")!)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerArquivo("COMPROVANTE_ENDERECO")}
                  title="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Outros Documentos */}
        {/* <div className="space-y-2">
          <Label className="text-base font-semibold">Outros Documentos</Label>
          <div className="space-y-4">
            {formData.documentos
              .filter(d => d.tipo_documento === 'OUTRO')
              .map((documento, index) => (
                <div key={index}>
                  {!documento.arquivo && !documento.caminho_arquivo ? (
                    <Card className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium">Documento Adicional {index + 1}</h4>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            const outrosDocs = formData.documentos.filter(d => d.tipo_documento === 'OUTRO');
                            const globalIndex = formData.documentos.indexOf(outrosDocs[index]);
                            removerDocumento(globalIndex);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <UploadsDragAndDrop
                        label=""
                        onFileSelect={(file) => {
                          const outrosDocs = formData.documentos.filter(d => d.tipo_documento === 'OUTRO');
                          const globalIndex = formData.documentos.indexOf(outrosDocs[index]);
                          handleFileUpload(globalIndex, file);
                        }}
                        currentFile={null}
                        className="p-4"
                      />
                    </Card>
                  ) : (
                    <div className="p-4 border rounded-lg bg-muted/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{documento.nome_arquivo}</p>
                          <p className="text-sm text-muted-foreground">
                            {documento.arquivo && formatFileSize(documento.arquivo.size)}
                            {documento.caminho_arquivo && "Arquivo salvo"}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => visualizarDocumento(documento)}
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadDocumento(documento)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            const outrosDocs = formData.documentos.filter(d => d.tipo_documento === 'OUTRO');
                            const globalIndex = formData.documentos.indexOf(outrosDocs[index]);
                            removerDocumento(globalIndex);
                          }}
                          title="Remover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            
            <Button 
              type="button" 
              onClick={() => {
                setFormData({
                  ...formData,
                  documentos: [
                    ...formData.documentos,
                    { tipo_documento: 'OUTRO', arquivo: null, nome_arquivo: '' }
                  ]
                });
              }} 
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" /> Adicionar Outro Documento
            </Button>
          </div>
        </div> */}

        {/* Resumo dos Documentos */}
        {(formData.documentos || []).filter(d => d.arquivo || d.caminho_arquivo).length > 0 && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2">Resumo dos Documentos Carregados:</h4>
            <div className="space-y-2">
              {(formData.documentos || [])
                .filter(d => d.arquivo || d.caminho_arquivo)
                .map((doc, index) => (
                  <div key={index} className="flex justify-between items-center text-sm p-2 bg-background rounded">
                    <span className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {doc.tipo_documento === 'RG' && 'RG'}
                      {doc.tipo_documento === 'CPF' && 'CPF'}
                      {doc.tipo_documento === 'CARTAO_SUS' && 'Cartão SUS'}
                      {doc.tipo_documento === 'COMPROVANTE_ENDERECO' && 'Comprovante de Endereço'}
                      {doc.tipo_documento === 'OUTRO' && 'Outro Documento'}
                    </span>
                    <span className="text-muted-foreground">{doc.nome_arquivo}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
